import type {
  ContractConstructorField,
  ContractConstructorGroup,
  ContractConstructorParams,
  ContractConstructorProduct,
  ContractConstructorSelectedProduct,
} from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplateConstructor.types'

export const PARENT_GROUP_CODES = new Set([
  'parent',
  'parent_fields',
])

export const PRODUCT_GROUP_CODES = new Set([
  'product',
  'service',
  'product_service',
  'product_service_fields',
])

export const GROUP_SECTION_TITLES: Record<string, string> = {
  student: 'Используемые поля учащегося',
  student_fields: 'Используемые поля учащегося',
  parent: 'Используемые поля родителя',
  parent_fields: 'Используемые поля родителя',
  contract: 'Используемые данные договора',
  contract_data: 'Используемые данные договора',
  product: 'Используемые данные продукта/услуги',
  service: 'Используемые данные продукта/услуги',
  product_service: 'Используемые данные продукта/услуги',
  product_service_fields: 'Используемые данные продукта/услуги',
  manual_fields: 'Используемые поля без автоматизации',
  unknown_fields: 'Поля из шаблона без каталога',
}

export const getGroupSectionTitle = (group: ContractConstructorGroup): string =>
  GROUP_SECTION_TITLES[group.code] || group.title

export const buildValuesFromGroups = (
  groups: ContractConstructorGroup[] = [],
): Record<string, string> => {
  const values: Record<string, string> = {}

  groups.forEach((group) => {
    group.items.forEach((item) => {
      values[item.code] = String(item.value ?? '').trim()
        ? String(item.value)
        : ''
    })
  })

  return values
}

const AMOUNT_WORDS_FIELD_BY_AMOUNT_FIELD: Record<string, string> = {
  contract_total_amount: 'contract_total_amount_words',
  final_amount_with_discount: 'final_amount_with_discount_words',
}

const ONES_MALE = [
  '',
  'один',
  'два',
  'три',
  'четыре',
  'пять',
  'шесть',
  'семь',
  'восемь',
  'девять',
]

const ONES_FEMALE = [
  '',
  'одна',
  'две',
  'три',
  'четыре',
  'пять',
  'шесть',
  'семь',
  'восемь',
  'девять',
]

const TEENS = [
  'десять',
  'одиннадцать',
  'двенадцать',
  'тринадцать',
  'четырнадцать',
  'пятнадцать',
  'шестнадцать',
  'семнадцать',
  'восемнадцать',
  'девятнадцать',
]

const TENS = [
  '',
  '',
  'двадцать',
  'тридцать',
  'сорок',
  'пятьдесят',
  'шестьдесят',
  'семьдесят',
  'восемьдесят',
  'девяносто',
]

const HUNDREDS = [
  '',
  'сто',
  'двести',
  'триста',
  'четыреста',
  'пятьсот',
  'шестьсот',
  'семьсот',
  'восемьсот',
  'девятьсот',
]

const THOUSAND_FORMS = ['тысяча', 'тысячи', 'тысяч'] as const
const MILLION_FORMS = ['миллион', 'миллиона', 'миллионов'] as const
const BILLION_FORMS = ['миллиард', 'миллиарда', 'миллиардов'] as const
const RUBLE_FORMS = ['рубль', 'рубля', 'рублей'] as const

const getPluralForm = (
  value: number,
  forms: readonly [string, string, string],
): string => {
  const absValue = Math.abs(value) % 100
  const lastDigit = absValue % 10

  if (absValue > 10 && absValue < 20) return forms[2]
  if (lastDigit > 1 && lastDigit < 5) return forms[1]
  if (lastDigit === 1) return forms[0]

  return forms[2]
}

const parseRublesAmount = (value: string): number | null => {
  const normalizedValue = String(value ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(',', '.')

  if (!normalizedValue) return null

  const parsedValue = Number(normalizedValue)

  if (!Number.isFinite(parsedValue) || parsedValue < 0) return null

  return Math.floor(parsedValue)
}

const getTripletWords = (
  value: number,
  gender: 'male' | 'female' = 'male',
): string[] => {
  const words: string[] = []
  const hundred = Math.floor(value / 100)
  const ten = Math.floor((value % 100) / 10)
  const one = value % 10

  if (HUNDREDS[hundred]) words.push(HUNDREDS[hundred])

  if (ten === 1) {
    words.push(TEENS[one])
    return words
  }

  if (TENS[ten]) words.push(TENS[ten])

  const ones = gender === 'female' ? ONES_FEMALE : ONES_MALE
  if (ones[one]) words.push(ones[one])

  return words
}

export const getRublesAmountInWords = (value: string): string => {
  const amount = parseRublesAmount(value)

  if (amount == null) return ''
  if (amount === 0) return 'ноль рублей'

  const parts = [
    {
      value: Math.floor(amount / 1_000_000_000),
      forms: BILLION_FORMS,
      gender: 'male' as const,
    },
    {
      value: Math.floor((amount % 1_000_000_000) / 1_000_000),
      forms: MILLION_FORMS,
      gender: 'male' as const,
    },
    {
      value: Math.floor((amount % 1_000_000) / 1_000),
      forms: THOUSAND_FORMS,
      gender: 'female' as const,
    },
    {
      value: amount % 1_000,
      forms: null,
      gender: 'male' as const,
    },
  ]

  const words = parts.flatMap((part) => {
    if (!part.value) return []

    return [
      ...getTripletWords(part.value, part.gender),
      ...(part.forms ? [getPluralForm(part.value, part.forms)] : []),
    ]
  })

  return [...words, getPluralForm(amount, RUBLE_FORMS)].join(' ')
}

export const applyAmountWordsAutofill = (
  values: Record<string, string>,
): Record<string, string> => {
  const nextValues = { ...values }

  Object.entries(AMOUNT_WORDS_FIELD_BY_AMOUNT_FIELD).forEach(
    ([amountFieldCode, amountWordsFieldCode]) => {
      const amountWords = getRublesAmountInWords(nextValues[amountFieldCode])

      if (amountWords) {
        nextValues[amountWordsFieldCode] = amountWords
      }
    },
  )

  return nextValues
}

export const getAmountWordsFieldCode = (code: string): string | null =>
  AMOUNT_WORDS_FIELD_BY_AMOUNT_FIELD[code] ?? null

export const getConstructorGroupFieldCodes = (
  groups: ContractConstructorGroup[] = [],
  groupCodes: Set<string>,
): string[] =>
  groups.flatMap((group) =>
    groupCodes.has(group.code) ? group.items.map((item) => item.code) : [],
  )

/** Обновить только поля нужных групп, остальную форму сохранить. */
export const mergeConstructorGroupValues = (
  prevValues: Record<string, string>,
  nextValues: Record<string, string>,
  fieldCodes: string[],
): Record<string, string> => {
  const merged = { ...prevValues }

  fieldCodes.forEach((code) => {
    merged[code] = nextValues[code] ?? ''
  })

  return merged
}

export const getEmptyRequiredFieldCodes = (
  groups: ContractConstructorGroup[] = [],
  values: Record<string, string> = {},
): string[] =>
  groups.flatMap((group) =>
    group.items
      .filter((item) => item.required && !String(values[item.code] ?? '').trim())
      .map((item) => item.code),
  )

export const isDateConstructorField = (code: string): boolean =>
  /_date$/.test(code) || code.endsWith('_at')

/** value селекта: custom | class:<class_user.id> | order:<direction_orders.id> */
export const getProductOptionKey = (
  product: ContractConstructorProduct,
): string => {
  if (product.type === 'custom' || product.id == null) {
    return 'custom'
  }

  return `${product.type}:${product.id}`
}

export const getSelectedProductKey = (
  selected: ContractConstructorSelectedProduct | null | undefined,
): string => {
  if (!selected || selected.type === 'custom' || selected.id == null) {
    return 'custom'
  }

  return `${selected.type}:${selected.id}`
}

export const parseProductOptionKey = (
  key: string,
): {
  product_type: 'custom' | 'class' | 'order'
  product_id: number | null
} => {
  if (key === 'custom') {
    return { product_type: 'custom', product_id: null }
  }

  const [productType, rawId] = key.split(':')
  const productId = Number(rawId)

  if (
    (productType === 'class' || productType === 'order') &&
    Number.isFinite(productId)
  ) {
    return { product_type: productType, product_id: productId }
  }

  return { product_type: 'custom', product_id: null }
}

/** Селектор «курс/класс» — если есть хотя бы один продукт кроме custom. */
export const hasSelectableProducts = (
  products: ContractConstructorProduct[] = [],
): boolean =>
  products.some((product) => product.type !== 'custom' && product.id != null)

/** @deprecated используй hasSelectableProducts */
export const hasOrderProducts = hasSelectableProducts

export const buildConstructorRequestParams = ({
  template_id,
  user_id,
  parent_id,
  productKey,
  includeProductParams = false,
}: {
  template_id: number
  user_id: number
  parent_id?: number | null
  productKey?: string
  includeProductParams?: boolean
}): ContractConstructorParams => {
  const params: ContractConstructorParams = {
    template_id,
    user_id,
  }

  if (parent_id != null) {
    params.parent_id = parent_id
  }

  if (!includeProductParams || !productKey) {
    return params
  }

  const { product_type, product_id } = parseProductOptionKey(productKey)

  if (
    (product_type === 'class' || product_type === 'order') &&
    product_id != null
  ) {
    params.product_type = product_type
    params.product_id = product_id
    return params
  }

  params.product_type = 'custom'
  return params
}

export const buildCreateContractPayload = ({
  user_id,
  template_id,
  document_name,
  parent_id,
  productKey,
  values,
}: {
  user_id: number
  template_id: number
  document_name: string
  parent_id?: number | null
  productKey: string
  values: Record<string, string>
}) => {
  const { product_type, product_id } = parseProductOptionKey(productKey)

  return {
    user_id,
    template_id,
    document_name,
    ...(parent_id != null ? { parent_id } : {}),
    product_type,
    ...(product_type !== 'custom' && product_id != null
      ? { product_id }
      : {}),
    values,
  }
}

export type ConstructorCalculatorField = ContractConstructorField & {
  formula: string
}

export const getConstructorCalculatorFields = (
  groups: ContractConstructorGroup[] = [],
): ConstructorCalculatorField[] =>
  groups.flatMap((group) =>
    group.items
      .filter((item) => item.type === 'calculator')
      .map((item) => {
        const formula = String(item.settings?.formula ?? '').trim()
        if (!formula) return null
        return { ...item, formula }
      })
      .filter((item): item is ConstructorCalculatorField => Boolean(item)),
  )
